import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/authContext";
import createAxiosInstance from "../../../../utils/axiosConfig";
import {
	FlowScore,
	FrameworkDomain,
	QuestionFlow,
} from "../../../../utils/interfaces";
import SideScreenLLM from "../sideScreen";
import QuestionFlowList from "./questionFlowList";
import AdminTables from "../../adminTables";
import ScoreHistoryIcon from "../../../../assets/svg/scoreHistoryIcon";
import EnhanceAI from "../../../../assets/svg/enhanceAI";
import ScoreHistory from "../sideScreen/scoreHistory";
import moment from "moment";
import Questions from "../questions";
import MaturityQuestionnaireSideScreen from "../../../private/maturityAssessment/maturityQuestionnaireSideScreen";
import { processStreamingHeaders } from "../../../../utils/axiosConfig";
import QuestionFlowEditSidebar from "../../questionFlowEditSidebar";
import EditQuestionsSidebar from "../../editQuestionsSidebar";
import { framework_domains } from "../../../../utils/endpoints";
import AddQuestionFlowSidebar from "../../addQuestionFlowSidebar";
import AddIcon from "../../../../assets/svg/addIcon";

const QuestionsFlow = ({
	frameworkId,
	selectedDomain,
	questionFlows,
	flowScores,
	latestScores,
	handleGoBack,
	handleRefresh,
	refresh,
}: {
	frameworkId: number;
	selectedDomain: FrameworkDomain;
	questionFlows: QuestionFlow[];
	flowScores: FlowScore[];
	latestScores: FlowScore[];
	handleGoBack: () => void;
	handleRefresh: () => Promise<void>;
	refresh?: number;
}) => {
	const navigate = useNavigate();
	const { userId, logout, userData } = useAuth();
	const [selectedQuestionFlow, setSelectedQuestionFlow] = useState<QuestionFlow | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [LLMContent, setLLMContent] = useState<any>("");
	const [currentLLMIndex, setCurrentLLMIndex] = useState<number>(0);
	const [payloads, setPayloads] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [isSideScreenVisible, setIsSideScreenVisible] = useState<boolean>(false);
	const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState<boolean>(false);
	// Add score history state management
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [historyLoading, setHistoryLoading] = useState<boolean>(false);
	const [historyContent, setHistoryContent] = useState<any[]>([]);

	const [eventSource, setEventSource] = useState<ReadableStreamDefaultReader | null>(null);
	const [firstChunkReceived, setFirstChunkReceived] = useState<boolean>(false);

	// Add refresh handling to QuestionsFlow component
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

	// Edit question flow states
	const [showQuestionFlowEditSidebar, setShowQuestionFlowEditSidebar] = useState<boolean>(false);
	const [editQuestionFlowData, setEditQuestionFlowData] = useState<QuestionFlow | null>(null);
	const [domains, setDomains] = useState<any[]>([]);

	// Edit questions states
	const [showEditQuestionsSidebar, setShowEditQuestionsSidebar] = useState<boolean>(false);
	const [editQuestionsFlowData, setEditQuestionsFlowData] = useState<QuestionFlow | null>(null);
	
	// Add question flow states
	const [showAddQuestionFlowSidebar, setShowAddQuestionFlowSidebar] = useState<boolean>(false);

	const calculateAverageScore = (scores: FlowScore[]) => {
		const totalScore = scores.reduce((sum, score) => sum + score.score, 0);
		return totalScore / questionFlows.length;
	};

	const getLatestDate = (scores: FlowScore[]) => {
		if (scores.length === 0) return "N/A";
		const latestScore = scores.reduce((latest, score) => {
			return new Date(score.score_date) > new Date(latest.score_date)
				? score
				: latest;
		});
		return format(new Date(latestScore.score_date), "MM/dd/yyyy");
	};

	const handleNext = () => {
		if (currentLLMIndex <= payloads.length - 1) {
			setCurrentLLMIndex(currentLLMIndex + 1);
			setLoading(true);
			setLLMContent(""); // Clear content to show spinner
			setFirstChunkReceived(false); // Reset flag for new request
		}
	};

	const handlePrev = () => {
		if (currentLLMIndex !== 0) {
			setCurrentLLMIndex(currentLLMIndex > 0 ? currentLLMIndex - 1 : 0);
			setLoading(true);
			setLLMContent(""); // Clear content to show spinner
			setFirstChunkReceived(false); // Reset flag for new request
		}
	};

	const closeChatHistory = () => {
		// Cancel any active EventSource
		if (eventSource) {
			eventSource.cancel();
			setEventSource(null);
		}
		
		// Reset all states
		setIsOpen(false);
		setLLMContent("");
		setLoading(false);
		setFirstChunkReceived(false);
	};

	const fetchDomainBasedUserAnswers = async (
		user_id: number,
		domain_id: number
	) => {
		try {
			console.log("Fetching user answers for domain:", domain_id);
			const response = await createAxiosInstance().get(
				`/user_answers/${user_id}/domain/${domain_id}`
			);

			if (response?.data?.status == 401 || response?.data?.status == 403) {
				// 401 is for expired token
				// 403 is for forbidden access (blocked by GPT.)
				console.log("user token expired");

				logout();
				navigate("/");
				return;
			}
			console.log("Received payloads:", response.data);
			setPayloads(response.data);
		} catch (e: any) {
			console.log(e, "error");
		}
	};

	const fetchLLMResponse = async (flowId: number, flowKey: string) => {
		try {
			// send request to /assessment endpoint.
			// show response in markdown Structure (set LLM content state.).

			// Close previous event source if it exists
			if (eventSource) {
				eventSource.cancel();
			}

			console.log("payloads", payloads, flowKey, flowId);
			const token = localStorage.getItem("token");
			if (payloads.length > 0) {
				const relevantPayload = payloads.find(
					(item: { framework_area: any }) => item.framework_area === flowKey
				);
				
				if (!relevantPayload) {
					console.error("No matching payload found for flowKey:", flowKey);
					setLLMContent("No assessment data found for this flow. Please complete the assessment first.");
					setLoading(false);
					return;
				}
				
				const payload = { ...relevantPayload, question_flow_id: flowId };

				// Send the payload to the /assessment endpoint
				const assessmentResponse = await fetch(
					process.env.REACT_APP_SERVER_URL + "/assessment",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify({ ...payload }),
					}
				);

				if (assessmentResponse?.status == 401 || assessmentResponse?.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("user token expired");
					logout();
					navigate("/");
					return;
				}

				// Process headers for token refresh
				processStreamingHeaders(assessmentResponse);

				if (assessmentResponse?.body) {
					const reader = assessmentResponse.body.getReader();
					setEventSource(reader);
				}
			} else {
				console.error("No payloads available");
				setLLMContent("No assessment data available. Please complete some assessments first.");
				setLoading(false);
			}
		} catch (e: any) {
			setLLMContent("");
			console.log(e, "error ");
			// More specific error handling logic here
			if (e instanceof TypeError) {
				// Handle network errors (e.g., network down, CORS issues)
				console.error("Network error:", e);
				return;
			} else {
				// Handle other types of errors
				console.error("Unexpected error:", e);
				return;
			}
		}
	};

	const addToLLMContent = (newContent: string) => {
		setLLMContent((prevContent: any) => `${prevContent}${newContent}`);
	};

	const handleAIButton = (flow: QuestionFlow) => {
		setIsSideScreenVisible(!isSideScreenVisible);
		setLoading(true);
		setIsOpen(true);
		setLLMContent("");
		setFirstChunkReceived(false); // Reset flag for new request
		fetchLLMResponse(flow.id, flow.description);
	};

	useEffect(() => {
		if (userId) {
			fetchDomainBasedUserAnswers(userId, selectedDomain.id);
		}
	}, [userId, selectedDomain.id]);

	// Fetch domains for edit functionality
	useEffect(() => {
		const fetchDomains = async () => {
			try {
				const domainsResponse = await createAxiosInstance().get(
					framework_domains(frameworkId)
				);
				if (domainsResponse.status === 200) {
					setDomains(domainsResponse.data);
				}
			} catch (error) {
				console.error('Error fetching domains:', error);
			}
		};

		if (frameworkId) {
			fetchDomains();
		}
	}, [frameworkId]);

	useEffect(() => {
		if (eventSource) {
			const readStream = async () => {
				try {
					setLoading(false);
					while (true) {
						const { done, value } = await eventSource.read();
						if (done) {
							break;
						}

						const buffer = new TextDecoder().decode(value, { stream: true });
						// Split buffer by newline characters
						const lines = buffer.split("\n");
						// Process complete lines
						lines.forEach((line) => {
							if (line.startsWith("data: ")) {
								const dataString = line.substring(6); // Remove 'data: ' prefix
								try {
									const data = JSON.parse(dataString);
									if (data.message) {
										addToLLMContent(data.message);
										if (!firstChunkReceived) {
											setFirstChunkReceived(true);
											setLoading(false);
										}
									}
								} catch (e) {
									console.error("Error parsing JSON:", e);
									return;
								}
							}
						});
					}
				} catch (e) {
					console.error("Error reading stream:", e);
					setEventSource(null);
					setLoading(false);
				} finally {
					setEventSource(null);
					setLoading(false);
				}
			};
			readStream();
		}
	}, [eventSource]);

	// Add helper functions for question flow table
	const handleQuestionFlowSelect = (flow: QuestionFlow) => {
		setSelectedQuestionFlow(flow);
		setIsQuestionnaireOpen(true);
	};

	const findScoreForQuestionFlow = (flowId: number) => {
		return flowScores
			.filter((score: any) => score.question_flow_id === flowId)
			.sort((a: any, b: any) => new Date(b.score_date).getTime() - new Date(a.score_date).getTime())[0];
	};

	// Add score history functions
	const closeScoreHistory = () => {
		setIsHistoryOpen(false);
		setHistoryContent([]);
	};

	const fetchScoreHistory = async (flowId: number) => {
		if (!userId) {
			window.location.href = "/";
			return;
		}
		setHistoryLoading(true);
		try {
			// Fetch score history for the specific question flow
			const scoreHistory = await createAxiosInstance().get(
				`history/user/${userId}/domain/${selectedDomain.id}`
			);

			if (scoreHistory.status == 401 || scoreHistory.status == 403) {
				console.log("user token expired");
				logout();
				navigate("/");
				return;
			}

		// The API returns the new flat array format
		setHistoryContent(scoreHistory?.data || []);
		} catch (e) {
			console.log(e);
			// If the endpoint doesn't exist, use the existing flow scores
			const flowScoreHistory = flowScores
				.filter((score: any) => score.question_flow_id === flowId)
				.sort((a: any, b: any) => new Date(b.score_date).getTime() - new Date(a.score_date).getTime());

		// Transform the fallback data to match the new API format (flat array)
		const transformedHistory = flowScoreHistory.map(score => {
			const flow = questionFlows.find(f => f.id === score.question_flow_id);
			
			return {
				date: moment(score.score_date).format("DD/MM/YYYY HH:mm:ss"),
				data: [{
					category_id: score.question_flow_id,
					category_title: flow?.title || 'Unknown Flow',
					total_score: score.score,
					active_domains_count: 1
				}]
			};
		});

		setHistoryContent(transformedHistory);
		} finally {
			setHistoryLoading(false);
		}
	};

	const handleHistoryOpen = (flowId: number) => {
		setIsHistoryOpen(true);
		fetchScoreHistory(flowId);
	};

	// Handle edit question flow
	const handleEditQuestionFlow = (questionFlow: QuestionFlow) => {
		setEditQuestionFlowData(questionFlow);
		setShowQuestionFlowEditSidebar(true);
	};

	// Handle close edit sidebar
	const handleCloseQuestionFlowEdit = () => {
		setShowQuestionFlowEditSidebar(false);
		setEditQuestionFlowData(null);
	};

	// Handle question flow edit success - refresh data
	const handleQuestionFlowEditSuccess = () => {
		// Trigger a refresh by calling the parent's refresh function
		handleRefresh();
	};

	// Handle edit questions
	const handleEditQuestions = (questionFlowId: number) => {
		const questionFlow = questionFlows.find(flow => flow.id === questionFlowId);
		if (questionFlow) {
			setEditQuestionsFlowData(questionFlow);
			setShowEditQuestionsSidebar(true);
		}
	};

	// Handle close edit questions sidebar
	const handleCloseEditQuestions = () => {
		setShowEditQuestionsSidebar(false);
		setEditQuestionsFlowData(null);
	};

	// Handle edit questions success - refresh data
	const handleEditQuestionsSuccess = () => {
		// Trigger a refresh by calling the parent's refresh function
		handleRefresh();
	};

	// Handle add question flow
	const handleAddQuestionFlow = () => {
		setShowAddQuestionFlowSidebar(true);
	};

	// Handle close add question flow sidebar
	const handleCloseAddQuestionFlow = () => {
		setShowAddQuestionFlowSidebar(false);
	};

	// Handle question flow add success - refresh data
	const handleQuestionFlowAddSuccess = () => {
		// Trigger a refresh by calling the parent's refresh function
		handleRefresh();
	};

	const headerTextStyle = {
		color: "#183FCE",
		fontSize: '12px',
		fontWeight: 'bold' as const
	};
	
	const tableColumns = [
		{
			accessorKey: "title",
			header: () => <span style={headerTextStyle}></span>,
			width: "30%",
			cell: ({ row }: { row: any }) => {
				const flow = row.original;
				const isSelected = selectedQuestionFlow?.id === flow.id;
				const isDisabled = !flow.is_enabled;
				return (
					<div onClick={() => {
						if (!isDisabled) {
							handleQuestionFlowSelect(flow);
						}
					}}>
						<span style={{
							color: isSelected ? "#FFFFFF" : (isDisabled ? "#B0B0B0" : "#2A2A2A"), 
							fontSize: '14px'
						}}>
							{flow.title}
						</span>
						<p style={{fontSize: '12px', color: isDisabled ? '#B0B0B0' : '#5376F0', fontWeight: 'bold', marginBottom: '0px'}}>
							{flow.description}
						</p>
					</div>
				);
			},
		},
		{
			id: "maturity_score",
			header: () => <span style={headerTextStyle}>Score</span>,
			width: "10%",
			cell: ({ row }: { row: any }) => {
				const flow = row.original;
				const score = findScoreForQuestionFlow(flow.id);
				const displayScore = score?.score ?? 0;
				const hasScore = score && score.score > 0;
				const isSelected = selectedQuestionFlow?.id === flow.id;
				const isDisabled = !flow.is_enabled;
				
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled ? "#B0B0B0" : (hasScore ? "#0027B0" : "#808080")), 
						fontSize: '14px', 
						textAlign: 'center'
					}}>
						{displayScore > 0 ? `${displayScore.toFixed(2)}` : "0.00"}
					</span>
				);
			},
		},
		{
			id: "assessment_date",
			header: () => <span style={headerTextStyle}>Date</span>,
			width: "15%",
			cell: ({ row }: { row: any }) => {
				const flow = row.original;
				const score = findScoreForQuestionFlow(flow.id);
				const isSelected = selectedQuestionFlow?.id === flow.id;
				const isDisabled = !flow.is_enabled;
				
				let displayDate = "N/A";
				if (score && score.score_date) {
					displayDate = format(new Date(score.score_date), "MM/dd/yyyy");
				}
				
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled ? "#B0B0B0" : (score ? "#2A2A2A" : "#808080")), 
						fontSize: '14px', 
						textAlign: 'center'
					}}>
						{displayDate}
					</span>
				);
			},
		},
		{
			id: "enhance_ai",
			header: () => <span style={headerTextStyle}>Enhance with AI</span>,
			width: "15%",
			cell: ({ row }: { row: any }) => {
				const flow = row.original;
				const isSelected = selectedQuestionFlow?.id === flow.id;
				const score = findScoreForQuestionFlow(flow.id);
				const hasScore = score && score.score > 0;
				const isDisabled = !flow.is_enabled;
				
				return (
					<div
						onClick={(e) => {
							e.stopPropagation();
							if (!isDisabled) {
								handleAIButton(flow);
							}
						}}
						style={{
							fontSize: '12px',
							padding: '4px 8px',
							borderColor: isSelected ? "#FFFFFF" : (isDisabled ? "#CCCCCC" : (hasScore ? "#0027B0" : "#CCCCCC")),
							color: isSelected ? "#FFFFFF" : (isDisabled ? "#CCCCCC" : (hasScore ? "#0027B0" : "#CCCCCC")),
							backgroundColor: "transparent",
							filter: isSelected ? 'brightness(0) invert(1)' : 'none',
							opacity: (isDisabled || !hasScore) ? 0.3 : 1,
							cursor: (isDisabled || !hasScore) ? 'not-allowed' : 'pointer',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
					>
						<EnhanceAI />
					</div>
				);
			},
		},
		// Add edit column only for admin users and FMA managers (last column)
		...((userData?.is_admin === 1 || userData?.account_type === 5) ? [{
			id: "edit",
			header: () => <span style={headerTextStyle}>Edit</span>,
			width: "5%",
			cell: ({ row }: { row: any }) => {
				const flow = row.original;
				const isSelected = selectedQuestionFlow?.id === flow.id;
				const isDisabled = !flow.is_enabled;
				return (
					<div 
						onClick={(e) => {
							e.stopPropagation();
							handleEditQuestionFlow(flow);
						}}
						style={{ 
							padding: '4px',
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'pointer',
							opacity: (!isDisabled) ? 1 : 0.3,
							filter: isSelected ? 'brightness(0) invert(1)' : 'none'
						}}
					>
						<i className="bi bi-pencil" style={{ fontSize: '14px' }}></i>
					</div>
				);
			},
		}] : []),
	];
	
	// Filter out question flows where is_deleted = true
	const activeQuestionFlows = questionFlows.filter(flow => flow.is_deleted !== true);
	
	// Just keep the existing component as is, but you can add a simple effect to log when props update
	useEffect(() => {
		console.log("QuestionsFlow: Received updated flow scores");
	}, [flowScores, latestScores]);

	return (
		<div className="container-fluid">
			<div className="row">
				<div className="col-md-12 mb-1">
					<div style={{ marginBottom: "15%" }} className="mt-3">
						{/* Header with Domain title and Add button */}
						{(userData?.is_admin === 1 || userData?.account_type === 5) ? (
							<div className="d-flex align-items-center mb-3">
								<h3 style={{paddingBottom: '0px', marginBottom: '0px', fontSize: '18px', fontWeight: 'bold', color: "#000000", marginRight: '15px'}}>
									{selectedDomain?.title}:
								</h3>
								<div className="ms-auto">
									<button
										type="button"
										className="btn d-flex align-items-center gap-2 text-white"
										onClick={handleAddQuestionFlow}
										style={{
											fontSize: '12px',
											// padding: '6px 12px',
											background: '#5376F0',
											borderRadius: '77.125rem',
											transition: 'all 0.3s ease',
										}}
										onMouseEnter={(e) => {
											const target = e.target as HTMLButtonElement;
											target.style.backgroundColor = '#26397E';
											target.style.transform = 'translateY(-1px)';
										}}
										onMouseLeave={(e) => {
											const target = e.target as HTMLButtonElement;
											target.style.backgroundColor = '#5376F0';
											target.style.transform = 'translateY(0)';
										}}
									>
										{/* <AddIcon /> */}
										ADD NEW
									</button>
								</div>
							</div>
						) : (
							<h3 style={{paddingBottom: '0px', marginBottom: '0px', fontSize: '18px', fontWeight: 'bold', color: "#000000"}}>{selectedDomain?.title}:</h3>
						)}
						<AdminTables
							data={activeQuestionFlows}
							columns={tableColumns}
							rowSelectionEnabled={true}
							onRowSelect={(flow) => {
								if (flow.is_enabled) {
									handleQuestionFlowSelect(flow);
								}
							}}
							showSearch={false}
							selectedRowId={selectedQuestionFlow?.id}
							selectedRowStyle={{
								backgroundColor: "#5376F0",
								color: "#FFFFFF"
							}}
							isRowDisabled={(flow) => !flow.is_enabled}
							containerStyle={{
								width: "100%",
								overflowX: "auto"
							}}
						/>
					</div>
				</div>
			</div>

			<MaturityQuestionnaireSideScreen 
				domainID={selectedDomain?.id} 
				questionFlowID={selectedQuestionFlow?.id} 
				isOpen={isQuestionnaireOpen}
				setIsOpen={(value: boolean) => {setIsQuestionnaireOpen(value); handleRefresh();}}
				frameworkId={frameworkId}
			/>
			{/* Side screen for AI conversation */}
			<SideScreenLLM
				isOpen={isOpen}
				handleClose={closeChatHistory}
				LLMContent={LLMContent}
				loading={loading}
				handleNext={handleNext}
				handlePrev={handlePrev}
				showPagination={false}
			/>

			{/* Score History side screen */}
			<ScoreHistory
				isOpen={isHistoryOpen}
				handleClose={closeScoreHistory}
				loading={historyLoading}
				content={historyContent}
			/>
			
			{/* Question Flow Edit Sidebar */}
			<QuestionFlowEditSidebar
				isOpen={showQuestionFlowEditSidebar}
				onClose={handleCloseQuestionFlowEdit}
				questionFlowData={editQuestionFlowData}
				domains={domains}
				onSuccess={handleQuestionFlowEditSuccess}
				onEditQuestions={handleEditQuestions}
			/>

			{/* Edit Questions Sidebar */}
			<EditQuestionsSidebar
				isOpen={showEditQuestionsSidebar}
				onClose={handleCloseEditQuestions}
				questionFlowData={editQuestionsFlowData}
				onSuccess={handleEditQuestionsSuccess}
			/>

			{/* Add Question Flow Sidebar */}
			<AddQuestionFlowSidebar
				isOpen={showAddQuestionFlowSidebar}
				onClose={handleCloseAddQuestionFlow}
				onSuccess={handleQuestionFlowAddSuccess}
				selectedDomain={selectedDomain}
				domains={domains}
			/>
		</div>
	);
};

export default QuestionsFlow;
